import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TrackedCompassPin } from '@repo/map-preferences/types';
import { CatalogGameMapFlags } from '@repo/types/catalog';
import type { MapData3dObject } from '@repo/types/maps/map-3d-types';
import type {
    GameMapInfo,
    GroupedCategory,
    IdBooleanObject,
    MapData,
    MapDataCategory,
    MapDataConfig,
    MapDataGameCoordinates,
    MapDataHeroAction,
    MapDataLocationFeature,
    MapDataSubcategory,
    MapInitializationData,
    PlayerLocationDeltas,
} from '@repo/types/maps/map-types';
import { mapsApi } from '../api/mapsApi';
import {
    CUSTOM_PINS_CATEGORY_ID,
    getFilteredFeatures,
    getLocationMappings,
    groupCategories,
    VIDEO_PINS_SUBCATEGORY_ID,
} from '../utils/mapData';

export interface FilterState {
    hiddenSubcategories: string[];
    hiddenRegions: string[];
    regionFilterMode: 'exclude' | 'include';
    hideAll: boolean;
    teleportOnly: boolean;
    unfoundOnly: boolean;
    searchTerm: string;
}

function arePlayerCoordsEqual(current: MapDataGameCoordinates | null, next: MapDataGameCoordinates | null): boolean {
    if (current === next) return true;
    if (!current || !next || current.x !== next.x || current.y !== next.y || current.z !== next.z) return false;
    if (current.metadata === next.metadata) return true;
    if (!current.metadata || !next.metadata) return false;

    const currentKeys = Object.keys(current.metadata);
    const nextKeys = Object.keys(next.metadata);
    return (
        currentKeys.length === nextKeys.length &&
        currentKeys.every(key => current.metadata?.[key] === next.metadata?.[key])
    );
}

export interface LayerVisibility {
    rasterTiles: boolean;
    labels: boolean;
    regions: boolean;
    objects3d: boolean;
    fogOfWar: boolean;
}

/** Visibility for activity heatmap event categories (e.g. kills / deaths). */
export type HeatmapEventVisibility = Record<string, boolean>;

/** Currently selected hotspot (heatmap event) marker on the map. */
export type SelectedHeatmapEvent = {
    id: string;
    /** Event category key from the heatmap API (e.g. `kills`, `deaths`). */
    eventType: string;
    coordinates: [number, number];
    /** Sample clip ids from the cluster (`videos.sample`), in API order. */
    videoIds?: string[];
};

export const MAP_LAYERS: Record<string, keyof LayerVisibility> = {
    '2d': 'rasterTiles',
    labels: 'labels',
    regions: 'regions',
    '3d': 'objects3d',
    'fog-of-war': 'fogOfWar',
};

export interface FogOfWarState {
    /** Reveal positions in Mercator meters [mx, my]. Each represents a VISUAL_REVEAL_RADIUS circle. */
    circles: Array<[number, number]>;
    /** Incremented on hydrate/clear to signal the rendering layer to rebuild its grid. */
    hydrationVersion: number;
}

export interface MapDataState {
    mapData: MapData | null;
    mapInfo: GameMapInfo[] | null;
    mapConfig: MapDataConfig | null;
    foundPins: IdBooleanObject;
    customPins: MapDataLocationFeature[];
    locationCategories: { [locationId: string]: MapDataCategory };
    locationSubcategories: { [locationId: string]: MapDataSubcategory };
    groupedCategories: GroupedCategory[];
    selectedMarkerId: string | null;
    trackedCompassPin: TrackedCompassPin | null;
    selectedHeatmapEvent: SelectedHeatmapEvent | null;
    filters: FilterState;
    layerVisibility: LayerVisibility;
    heatmapEventVisibility: HeatmapEventVisibility;
    isAddingCustomLocation: boolean;
    editingCustomPinState: {
        isEditing: boolean;
        pinId: string | null;
        isNewlyCreated?: boolean;
        pinData?: {
            coordinates: [number, number] | null;
            title: string;
            description: string;
            color: string;
        } | null;
    };
    isInCreatorMode: boolean;
    isAddingMapLocation: boolean;
    isEditingMapLocation: boolean;
    editingMapLocationId: string | null;
    isMovingLocation: boolean;
    movingLocationId: string | null;
    isBulkMovingLocations: boolean;
    isBulkSelectingPins: boolean;
    isBulkMovingSelection: boolean;
    selectedMarkerIds: string[];
    isLocalPreview: boolean;
    isAddingMapLabel: boolean;
    isMovingLabel: boolean;
    movingLabelId: string | null;
    selectedLabelId: string | null;
    selectedRegionId: string | null;
    previewMode: boolean;
    playerCoords: MapDataGameCoordinates | null;
    playerBearing: number | null;
    trainerConnected: boolean;
    locationEnabled: boolean;
    followEnabled: boolean;
    teleportEnabled: boolean;
    tooltipDelayFinished: boolean;
    lastSelectedCategoryId: string | null;
    lastSelectedSubcategoryId: string | null;
    fogOfWar: FogOfWarState;
}

const initialState: MapDataState = {
    mapData: null,
    mapInfo: [],
    mapConfig: null,
    foundPins: {},
    customPins: [],
    locationCategories: {},
    locationSubcategories: {},
    groupedCategories: [],
    selectedMarkerId: null,
    trackedCompassPin: null,
    selectedHeatmapEvent: null,
    filters: {
        hiddenSubcategories: [],
        hiddenRegions: [],
        regionFilterMode: 'exclude',
        hideAll: false,
        teleportOnly: false,
        unfoundOnly: false,
        searchTerm: '',
    },
    layerVisibility: {
        rasterTiles: true,
        labels: true,
        regions: true,
        objects3d: false,
        fogOfWar: false,
    },
    // Default: kills on, deaths off.
    heatmapEventVisibility: {
        kills: true,
        deaths: false,
    },
    isAddingCustomLocation: false,
    editingCustomPinState: {
        isEditing: false,
        pinId: null,
        isNewlyCreated: false,
        pinData: null,
    },
    isInCreatorMode: false,
    isAddingMapLocation: false,
    isEditingMapLocation: false,
    editingMapLocationId: null,
    isMovingLocation: false,
    movingLocationId: null,
    isBulkMovingLocations: false,
    isBulkSelectingPins: false,
    isBulkMovingSelection: false,
    selectedMarkerIds: [],
    isLocalPreview: false,
    isAddingMapLabel: false,
    isMovingLabel: false,
    movingLabelId: null,
    selectedLabelId: null,
    selectedRegionId: null,
    previewMode: false,
    playerCoords: null,
    playerBearing: null,
    trainerConnected: false,
    locationEnabled: false,
    teleportEnabled: false,
    followEnabled: false,
    tooltipDelayFinished: false,
    lastSelectedCategoryId: null,
    lastSelectedSubcategoryId: null,
    fogOfWar: {
        circles: [],
        hydrationVersion: 0,
    },
};

const getSubcategoryIdsForCategory = (mapData: MapData, categoryId: string) => {
    const subcategoryIds = mapData.metadata.subcategories
        .filter(sub => sub.categoryExternalId === categoryId)
        .map(sub => sub.externalId);

    if (categoryId === CUSTOM_PINS_CATEGORY_ID) {
        return [...new Set([...subcategoryIds, CUSTOM_PINS_CATEGORY_ID, VIDEO_PINS_SUBCATEGORY_ID])];
    }

    return subcategoryIds;
};

const getAllSubcategoryIds = (mapData: MapData) => {
    const subcategoryIds = mapData.metadata.subcategories.map(sub => sub.externalId);
    return [...new Set([...subcategoryIds, CUSTOM_PINS_CATEGORY_ID, VIDEO_PINS_SUBCATEGORY_ID])];
};

const mapDataSlice = createSlice({
    name: 'mapData',
    initialState,
    reducers: {
        setCompleteMapData: (state, action: PayloadAction<MapInitializationData>) => {
            const { mapData, mapInfo, mapConfig } = action.payload;
            const { categories, subcategories } = getLocationMappings(mapData);

            const hiddenByDefaultCategories = mapData.metadata.categories.filter(
                category => !category.visibleByDefault,
            );

            // Get all subcategories that belong to hidden-by-default categories
            const hiddenByDefaultSubcategories = mapData.metadata.subcategories
                .filter(sub => hiddenByDefaultCategories.some(cat => cat.externalId === sub.categoryExternalId))
                .map(sub => sub.externalId);

            const lastSelectedCategoryId = state.lastSelectedCategoryId;
            const lastSelectedSubcategoryId = state.lastSelectedSubcategoryId;

            // When switching to a different map, wipe previous-map filter/layer state so
            // stale toggles (hideAll, teleportOnly, hiddenRegions, etc.) don't leak across.
            // The preferences sync re-applies any stored per-map values on top of this reset.
            const isDifferentMap =
                state.mapConfig?.externalId !== undefined && state.mapConfig.externalId !== mapConfig.externalId;
            if (isDifferentMap) {
                state.filters = { ...initialState.filters };
                state.layerVisibility = { ...initialState.layerVisibility };
                state.heatmapEventVisibility = { ...initialState.heatmapEventVisibility };
                // Pin ids are per-map, so a selection carried across maps would target rows
                // that are not on screen.
                state.selectedMarkerIds = [];
                state.trackedCompassPin = null;
                state.playerCoords = null;
                state.playerBearing = null;
                state.trainerConnected = false;
                state.locationEnabled = false;
                state.isBulkSelectingPins = false;
                state.isBulkMovingSelection = false;
            }

            // biome-ignore lint/suspicious/noExplicitAny: TODO figure out a better way to type this with readonly
            state.mapData = mapData as any;
            state.mapInfo = [...mapInfo];
            state.mapConfig = mapConfig;
            state.locationCategories = categories;
            state.locationSubcategories = subcategories;
            state.groupedCategories = groupCategories(mapData, state.foundPins);
            // In mini-map mode (checklist pages), filter state is managed externally
            // by MapController — skip appending hidden-by-default subcategories so
            // the checklist filters are not overridden.
            if (!mapConfig.isMiniMapMode) {
                state.filters = {
                    ...state.filters,
                    hiddenSubcategories: [...state.filters.hiddenSubcategories, ...hiddenByDefaultSubcategories],
                };
            }

            state.lastSelectedCategoryId = lastSelectedCategoryId;
            state.lastSelectedSubcategoryId = lastSelectedSubcategoryId;

            // Reset layer/fog/heatmap selection so stale values from the previous map don't apply before DO sync lands.
            state.layerVisibility = { ...initialState.layerVisibility };
            state.heatmapEventVisibility = { ...initialState.heatmapEventVisibility };
            state.selectedHeatmapEvent = null;
            state.fogOfWar = { circles: [], hydrationVersion: state.fogOfWar.hydrationVersion + 1 };
        },

        updateMapData: (state, action: PayloadAction<MapData>) => {
            const mapData = action.payload;
            const { categories, subcategories } = getLocationMappings(mapData);

            const hiddenByDefaultCategories = mapData.metadata.categories.filter(
                category => !category.visibleByDefault,
            );

            // Get all subcategories that belong to hidden-by-default categories
            const hiddenByDefaultSubcategories = mapData.metadata.subcategories
                .filter(sub => hiddenByDefaultCategories.some(cat => cat.externalId === sub.categoryExternalId))
                .map(sub => sub.externalId);

            // biome-ignore lint/suspicious/noExplicitAny: TODO figure out a better way to type this with readonly
            state.mapData = mapData as any;
            state.locationCategories = categories;
            state.locationSubcategories = subcategories;
            state.groupedCategories = groupCategories(mapData, state.foundPins);
            state.filters = {
                ...state.filters,
                hiddenSubcategories: [
                    ...new Set([...state.filters.hiddenSubcategories, ...hiddenByDefaultSubcategories]),
                ],
            };
        },

        updateFoundPins: (state, action: PayloadAction<IdBooleanObject>) => {
            if (!state.mapData) return;
            state.foundPins = { ...state.foundPins, ...action.payload };

            // Also update the found property on the corresponding features
            Object.entries(action.payload).forEach(([locationId, found]) => {
                if (!state.mapData) return;
                const featureIndex = state.mapData.geojson.features.findIndex(
                    f => f.properties.externalId === locationId,
                );
                if (featureIndex !== -1 && state.mapData.geojson.features[featureIndex]) {
                    state.mapData.geojson.features[featureIndex].properties.found = found;
                }
            });

            state.groupedCategories = groupCategories(state.mapData, state.foundPins);
        },

        setFoundPins: (state, action: PayloadAction<IdBooleanObject>) => {
            state.foundPins = action.payload;
            if (state.mapData) {
                state.groupedCategories = groupCategories(state.mapData, state.foundPins);
            }
        },

        selectMarker: (state, action: PayloadAction<string>) => {
            state.selectedMarkerId = action.payload;
            state.selectedHeatmapEvent = null;
        },

        clearSelectedMarker: state => {
            state.selectedMarkerId = null;
        },

        setTrackedCompassPin: (state, action: PayloadAction<TrackedCompassPin | null>) => {
            state.trackedCompassPin = action.payload;
        },

        selectHeatmapEvent: (state, action: PayloadAction<SelectedHeatmapEvent>) => {
            state.selectedHeatmapEvent = action.payload;
            state.selectedMarkerId = null;
        },

        clearSelectedHeatmapEvent: state => {
            state.selectedHeatmapEvent = null;
        },

        addCustomMarker: (state, action: PayloadAction<MapDataLocationFeature>) => {
            if (!state.mapData) return;

            state.mapData.geojson.features.push(action.payload);
            state.customPins.push(action.payload);

            const { categories, subcategories } = getLocationMappings(state.mapData);
            Object.assign(state, {
                locationCategories: categories,
                locationSubcategories: subcategories,
                groupedCategories: groupCategories(state.mapData, state.foundPins),
            });
        },

        addMapLocationFeature: (state, action: PayloadAction<MapDataLocationFeature>) => {
            if (!state.mapData) return;

            const alreadyExists = state.mapData.geojson.features.some(
                f => f.properties.externalId === action.payload.properties.externalId,
            );
            if (alreadyExists) return;

            state.mapData.geojson.features.push(action.payload);

            const { categories, subcategories } = getLocationMappings(state.mapData);
            Object.assign(state, {
                locationCategories: categories,
                locationSubcategories: subcategories,
                groupedCategories: groupCategories(state.mapData, state.foundPins),
            });
        },

        setCustomPins: (state, action: PayloadAction<MapDataLocationFeature[]>) => {
            state.customPins = action.payload;

            if (state.mapData) {
                state.mapData.geojson.features = state.mapData.geojson.features.filter(
                    feature => !feature.properties.isCustomPin,
                );

                state.mapData.geojson.features.push(...action.payload);

                const { categories, subcategories } = getLocationMappings(state.mapData);
                state.locationCategories = categories;
                state.locationSubcategories = subcategories;
                state.groupedCategories = groupCategories(state.mapData, state.foundPins);
            }
        },

        refreshCachedLocationCategories: state => {
            if (!state.mapData) return;
            const { categories, subcategories } = getLocationMappings(state.mapData);
            state.locationCategories = categories;
            state.locationSubcategories = subcategories;
        },

        toggleCategoryFilter: (state, action: PayloadAction<string>) => {
            const categoryId = action.payload;

            if (!state.mapData) return;

            const categorySubcategories = getSubcategoryIdsForCategory(state.mapData, categoryId);

            // Check if any subcategories are currently visible (not hidden)
            const hasVisibleSubcategories = categorySubcategories.some(
                subId => !state.filters.hiddenSubcategories.includes(subId),
            );

            if (hasVisibleSubcategories) {
                // Hide all subcategories in this category
                state.filters.hiddenSubcategories = [
                    ...state.filters.hiddenSubcategories,
                    ...categorySubcategories.filter(subId => !state.filters.hiddenSubcategories.includes(subId)),
                ];
            } else {
                // Show all subcategories in this category
                state.filters.hiddenSubcategories = state.filters.hiddenSubcategories.filter(
                    subId => !categorySubcategories.includes(subId),
                );
            }

            // Reset hideAll flag when user manually toggles categories
            if (state.filters.hideAll) {
                state.filters.hideAll = false;
            }
        },

        showOnlyCategoryPins: (state, action: PayloadAction<string>) => {
            const categoryId = action.payload;
            if (!state.mapData) return;

            const visibleSubcategories = new Set(getSubcategoryIdsForCategory(state.mapData, categoryId));
            state.filters.hiddenSubcategories = getAllSubcategoryIds(state.mapData).filter(
                subcategoryId => !visibleSubcategories.has(subcategoryId),
            );
            state.filters.hideAll = false;
            state.filters.hiddenRegions = [];
            state.filters.regionFilterMode = 'exclude';
        },

        showOnlySubcategoryPins: (state, action: PayloadAction<string>) => {
            const subcategoryId = action.payload;
            if (!state.mapData) return;

            state.filters.hiddenSubcategories = getAllSubcategoryIds(state.mapData).filter(id => id !== subcategoryId);
            state.filters.hideAll = false;
            state.filters.hiddenRegions = [];
            state.filters.regionFilterMode = 'exclude';
        },

        toggleSubcategoryFilter: (state, action: PayloadAction<string>) => {
            const subcategoryId = action.payload;
            const current = state.filters.hiddenSubcategories;
            const isCurrentlyHidden = current.includes(subcategoryId);

            state.filters.hiddenSubcategories = isCurrentlyHidden
                ? current.filter(id => id !== subcategoryId)
                : [...current, subcategoryId];

            // No need to manage parent categories anymore since we only track subcategories

            // Reset hideAll flag when user manually toggles subcategories
            if (state.filters.hideAll) {
                state.filters.hideAll = false;
            }
        },

        toggleRegionPinVisibility: (state, action: PayloadAction<string>) => {
            const regionId = action.payload;
            const isCurrentlyHidden = state.filters.hiddenRegions.includes(regionId);

            state.filters.hiddenRegions = isCurrentlyHidden
                ? state.filters.hiddenRegions.filter(id => id !== regionId)
                : [...state.filters.hiddenRegions, regionId];
            state.filters.regionFilterMode = 'exclude';
        },

        showOnlyRegionPins: (state, action: PayloadAction<string>) => {
            const regionId = action.payload;
            const regions = state.mapData?.regions?.features;
            if (!regions?.length) return;

            state.filters.hiddenSubcategories = [];
            state.filters.hideAll = false;
            state.filters.hiddenRegions = regions
                .map(region => region.properties.externalId)
                .filter(externalId => externalId !== regionId);
            state.filters.regionFilterMode = 'include';
        },

        showAllRegionPins: state => {
            state.filters.hiddenRegions = [];
            state.filters.regionFilterMode = 'exclude';
        },

        hideAllRegionPins: state => {
            const regions = state.mapData?.regions?.features;
            if (!regions?.length) return;

            state.filters.hiddenRegions = regions.map(region => region.properties.externalId);
            state.filters.regionFilterMode = 'exclude';
        },

        setHideAll: (state, action: PayloadAction<boolean>) => {
            const hideAll = action.payload;
            state.filters.hideAll = hideAll;

            if (hideAll && state.mapData) {
                state.filters.hiddenSubcategories = getAllSubcategoryIds(state.mapData);
            } else if (!hideAll) {
                // Show all by clearing hidden subcategories array
                state.filters.hiddenSubcategories = [];
            }
        },

        toggleTeleportOnly: state => {
            state.filters.teleportOnly = !state.filters.teleportOnly;
        },

        toggleUnfoundOnly: state => {
            state.filters.unfoundOnly = !state.filters.unfoundOnly;
        },

        setSearchTerm: (state, action: PayloadAction<string>) => {
            state.filters.searchTerm = action.payload;
        },

        setFilterStateFromUrl: (state, action: PayloadAction<Partial<FilterState>>) => {
            const newFilters = action.payload;

            state.filters = {
                ...state.filters,
                ...newFilters,
            };

            // If hideAll is set to true from URL, populate the hidden array with all subcategories
            if (newFilters.hideAll && state.mapData) {
                state.filters.hiddenSubcategories = getAllSubcategoryIds(state.mapData);
            }
        },

        setIsAddingCustomLocation: (state, action: PayloadAction<boolean>) => {
            state.isAddingCustomLocation = action.payload;
        },

        setCustomPinEditingState: (
            state,
            action: PayloadAction<{
                isEditing: boolean;
                pinId: string | null;
                isNewlyCreated?: boolean;
                pinData?: {
                    coordinates: [number, number] | null;
                    title: string;
                    description: string;
                    color: string;
                } | null;
            }>,
        ) => {
            state.editingCustomPinState = action.payload;
        },

        toggleCreatorMode: state => {
            state.isInCreatorMode = !state.isInCreatorMode;
        },

        resetAllFilters: state => {
            // When resetting filters (e.g., from show/hide all toggle), show all categories
            // The visibleByDefault logic should only apply during initial map load
            state.filters = {
                hiddenSubcategories: [],
                hiddenRegions: [],
                regionFilterMode: 'exclude',
                hideAll: false,
                teleportOnly: false,
                unfoundOnly: false,
                searchTerm: '',
            };
        },

        setIsAddingMapLocation: (state, action: PayloadAction<boolean>) => {
            state.isAddingMapLocation = action.payload;
        },
        setIsEditingMapLocation: (state, action: PayloadAction<{ isEditing: boolean; locationId: string | null }>) => {
            state.isEditingMapLocation = action.payload.isEditing;
            state.editingMapLocationId = action.payload.locationId;
        },
        setIsMovingLocation: (state, action: PayloadAction<{ isMoving: boolean; locationId: string | null }>) => {
            state.isMovingLocation = action.payload.isMoving;
            state.movingLocationId = action.payload.locationId;
        },
        setIsBulkMovingLocations: (state, action: PayloadAction<boolean>) => {
            state.isBulkMovingLocations = action.payload;
        },
        setIsBulkSelectingPins: (state, action: PayloadAction<boolean>) => {
            // Leaving selection mode must keep the selection: the bulk edit form acts on it afterwards.
            state.isBulkSelectingPins = action.payload;
        },
        toggleMarkerInSelection: (state, action: PayloadAction<string>) => {
            const index = state.selectedMarkerIds.indexOf(action.payload);
            if (index === -1) {
                state.selectedMarkerIds.push(action.payload);
                return;
            }
            state.selectedMarkerIds.splice(index, 1);
        },
        setSelectedMarkerIds: (state, action: PayloadAction<string[]>) => {
            state.selectedMarkerIds = [...new Set(action.payload)];
        },
        clearSelectedMarkerIds: state => {
            state.selectedMarkerIds = [];
        },
        setIsLocalPreview: (state, action: PayloadAction<boolean>) => {
            state.isLocalPreview = action.payload;
        },
        setIsBulkMovingSelection: (state, action: PayloadAction<boolean>) => {
            state.isBulkMovingSelection = action.payload;
        },
        updateMapLocationFeatures: (state, action: PayloadAction<MapDataLocationFeature[]>) => {
            if (!state.mapData) return;

            const customFeatures = state.mapData.geojson.features.filter(feature => feature.properties.isCustomPin);
            const movedFeatures = action.payload
                .filter(feature => !feature.properties.isCustomPin)
                .map(feature => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        found: state.foundPins[feature.properties.externalId] || false,
                        selected: state.selectedMarkerId === feature.properties.externalId,
                    },
                }));

            state.mapData.geojson.features = [...movedFeatures, ...customFeatures];

            const { categories, subcategories } = getLocationMappings(state.mapData);
            state.locationCategories = categories;
            state.locationSubcategories = subcategories;
            state.groupedCategories = groupCategories(state.mapData, state.foundPins);
        },
        setIsAddingMapLabel: (state, action: PayloadAction<boolean>) => {
            state.isAddingMapLabel = action.payload;
        },
        setIsMovingLabel: (state, action: PayloadAction<{ isMoving: boolean; labelId: string | null }>) => {
            state.isMovingLabel = action.payload.isMoving;
            state.movingLabelId = action.payload.labelId;
        },
        setSelectedLabelId: (state, action: PayloadAction<string | null>) => {
            state.selectedLabelId = action.payload;
        },
        setSelectedRegionId: (state, action: PayloadAction<string | null>) => {
            state.selectedRegionId = action.payload;
        },
        setPreviewMode: (state, action: PayloadAction<boolean>) => {
            state.previewMode = action.payload;
        },

        setPlayerCoords: (state, action: PayloadAction<MapDataGameCoordinates | null>) => {
            if (arePlayerCoordsEqual(state.playerCoords, action.payload)) return;
            state.playerCoords = action.payload;
        },

        setPlayerBearing: (state, action: PayloadAction<number | null>) => {
            state.playerBearing = action.payload;
        },

        setTrainerConnected: (state, action: PayloadAction<boolean>) => {
            state.trainerConnected = action.payload;
        },

        setLocationEnabled: (state, action: PayloadAction<boolean>) => {
            state.locationEnabled = action.payload;
        },
        setTeleportEnabled: (state, action: PayloadAction<boolean>) => {
            state.teleportEnabled = action.payload;
        },
        toggleFollowEnabled: state => {
            state.followEnabled = !state.followEnabled;
        },
        setFollowEnabled: (state, action: PayloadAction<boolean>) => {
            state.followEnabled = action.payload;
        },
        setTooltipDelayFinished: (state, action: PayloadAction<boolean>) => {
            state.tooltipDelayFinished = action.payload;
        },
        setPlayerLocationDeltas: (state, action: PayloadAction<PlayerLocationDeltas>) => {
            if (state.mapData) {
                state.mapData.metadata.playerLocationDeltas = action.payload;
            }
        },
        setLastSelectedCategory: (state, action: PayloadAction<string | null>) => {
            state.lastSelectedCategoryId = action.payload;
        },
        setLastSelectedSubcategory: (state, action: PayloadAction<string | null>) => {
            state.lastSelectedSubcategoryId = action.payload;
        },
        toggleLayerVisibility: (state, action: PayloadAction<keyof LayerVisibility>) => {
            const layer = action.payload;
            state.layerVisibility[layer] = !state.layerVisibility[layer];
        },
        setLayerVisibility: (state, action: PayloadAction<Partial<LayerVisibility>>) => {
            state.layerVisibility = { ...state.layerVisibility, ...action.payload };
        },
        toggleHeatmapEventVisibility: (state, action: PayloadAction<string>) => {
            const eventType = action.payload;
            state.heatmapEventVisibility[eventType] = !state.heatmapEventVisibility[eventType];
        },
        setHeatmapEventVisibility: (state, action: PayloadAction<Partial<HeatmapEventVisibility>>) => {
            for (const [key, value] of Object.entries(action.payload)) {
                if (typeof value === 'boolean') {
                    state.heatmapEventVisibility[key] = value;
                }
            }
        },
        toggleAllHeatmapEventsVisibility: (state, action: PayloadAction<string[] | undefined>) => {
            const keys = action.payload?.length
                ? action.payload
                : (Object.keys(state.heatmapEventVisibility) as string[]);
            const allVisible = keys.every(key => state.heatmapEventVisibility[key]);
            const next = !allVisible;
            for (const key of keys) {
                state.heatmapEventVisibility[key] = next;
            }
        },
        clearFog: state => {
            state.fogOfWar.circles = [];
            state.fogOfWar.hydrationVersion += 1;
        },
        addFogCircles: (state, action: PayloadAction<Array<[number, number]>>) => {
            if (action.payload.length === 0) return;
            for (const pos of action.payload) {
                state.fogOfWar.circles.push(pos);
            }
        },
        hydrateFogCircles: (state, action: PayloadAction<Array<[number, number]>>) => {
            state.fogOfWar.circles = action.payload;
            state.fogOfWar.hydrationVersion += 1;
        },
        updateMapObject3dData: (
            state,
            action: PayloadAction<{ externalId: string; updatedObject: MapData3dObject }>,
        ) => {
            if (!state.mapData?.objects3d?.features) return;

            const index = state.mapData.objects3d.features.findIndex(
                obj => obj.properties.externalId === action.payload.externalId,
            );

            if (index !== -1) {
                state.mapData.objects3d.features[index] = action.payload.updatedObject;
            }
        },
    },

    // RTK Query integration - listen to API state changes
    extraReducers: builder => {
        // When map data is fetched successfully
        builder.addMatcher(mapsApi.endpoints.getGameMap.matchFulfilled, (state, action) => {
            // Local preview data comes from a file and is the source of truth for this session;
            // a background refetch of the shell map must not replace it.
            if (state.isLocalPreview) {
                return;
            }

            const incomingMapData = action.payload;
            const incomingFeatureIds = new Set(incomingMapData.geojson.features.map(f => f.properties.externalId));

            // Preserve optimistically added pins (e.g. created in creator mode) that aren't in the
            // server response yet - they may not be published/approved yet
            const existingFeatures = state.mapData?.geojson.features ?? [];
            const preservedFeatures = existingFeatures.filter(f => !incomingFeatureIds.has(f.properties.externalId));

            const mergedFeatures =
                preservedFeatures.length > 0
                    ? [...incomingMapData.geojson.features, ...preservedFeatures]
                    : incomingMapData.geojson.features;

            const mapData = {
                ...incomingMapData,
                geojson: {
                    ...incomingMapData.geojson,
                    features: mergedFeatures,
                },
            };

            const lastSelectedCategoryId = state.lastSelectedCategoryId;
            const lastSelectedSubcategoryId = state.lastSelectedSubcategoryId;

            const { categories, subcategories } = getLocationMappings(mapData);

            // biome-ignore lint/suspicious/noExplicitAny: TODO figure out a better way to type this with readonly
            state.mapData = mapData as any;
            state.locationCategories = categories;
            state.locationSubcategories = subcategories;
            state.groupedCategories = groupCategories(mapData, state.foundPins);

            state.lastSelectedCategoryId = lastSelectedCategoryId;
            state.lastSelectedSubcategoryId = lastSelectedSubcategoryId;
        });

        // When found pins are fetched successfully
        builder.addMatcher(mapsApi.endpoints.getFoundPins.matchFulfilled, (state, action) => {
            const foundPinsArray = action.payload;

            const foundPinsObj = foundPinsArray.reduce((acc, pin) => {
                acc[pin.pinnableId] = pin.found;
                return acc;
            }, {} as IdBooleanObject);

            state.foundPins = foundPinsObj;
            if (state.mapData) {
                // Mutate features in place so Immer doesn't create a new mapData
                // reference — a new reference triggers source.setData() in
                // useMapInitializer which causes a full map refresh/flash.
                for (const feature of state.mapData.geojson.features) {
                    feature.properties.found = foundPinsObj[feature.properties.externalId] || false;
                }
                state.groupedCategories = groupCategories(state.mapData, foundPinsObj);
            }
        });

        // When a pin toggle is completed successfully
        builder.addMatcher(mapsApi.endpoints.togglePinFound.matchFulfilled, (state, action) => {
            const pin = action.payload;
            const { locationId } = action.meta.arg.originalArgs;

            state.foundPins[locationId] = pin.found;

            if (state.mapData) {
                const featureIndex = state.mapData.geojson.features.findIndex(
                    f => f.properties.externalId === locationId,
                );

                if (featureIndex !== -1 && state.mapData.geojson.features[featureIndex]) {
                    state.mapData.geojson.features[featureIndex].properties.found = pin.found;
                }
            }
        });

        // When all pins are reset successfully
        builder.addMatcher(mapsApi.endpoints.resetAllFoundPins.matchFulfilled, state => {
            state.foundPins = {};

            if (state.mapData) {
                const updatedMapData = {
                    ...state.mapData,
                    geojson: {
                        ...state.mapData.geojson,
                        features: state.mapData.geojson.features.map(feature => ({
                            ...feature,
                            properties: { ...feature.properties, found: false },
                        })),
                    },
                };

                state.mapData = updatedMapData;
                state.groupedCategories = groupCategories(updatedMapData, state.foundPins);
            }
        });

        // When custom markers are fetched successfully
        builder.addMatcher(mapsApi.endpoints.getCustomMarkers.matchFulfilled, (state, action) => {
            const customPins = action.payload;
            state.customPins = customPins;

            // Add custom pins to the map data if it exists
            if (state.mapData) {
                // Remove existing custom pins
                state.mapData.geojson.features = state.mapData.geojson.features.filter(
                    feature => !feature.properties.isCustomPin,
                );

                // Add new custom pins
                state.mapData.geojson.features.push(...customPins);

                const { categories, subcategories } = getLocationMappings(state.mapData);
                state.locationCategories = categories;
                state.locationSubcategories = subcategories;
                state.groupedCategories = groupCategories(state.mapData, state.foundPins);
            }
        });
    },
});

export const {
    setCompleteMapData,
    updateMapData,
    updateFoundPins,
    setFoundPins,
    selectMarker,
    clearSelectedMarker,
    setTrackedCompassPin,
    selectHeatmapEvent,
    clearSelectedHeatmapEvent,
    addCustomMarker,
    addMapLocationFeature,
    setCustomPins,
    refreshCachedLocationCategories,
    toggleCategoryFilter,
    showOnlyCategoryPins,
    showOnlySubcategoryPins,
    toggleSubcategoryFilter,
    toggleRegionPinVisibility,
    showOnlyRegionPins,
    showAllRegionPins,
    hideAllRegionPins,
    setHideAll,
    toggleTeleportOnly,
    toggleUnfoundOnly,
    setSearchTerm,
    setFilterStateFromUrl,
    setIsAddingCustomLocation,
    setCustomPinEditingState,
    toggleCreatorMode,
    resetAllFilters,
    setIsAddingMapLocation,
    setIsEditingMapLocation,
    setIsMovingLocation,
    setIsBulkMovingLocations,
    setIsBulkSelectingPins,
    toggleMarkerInSelection,
    setSelectedMarkerIds,
    clearSelectedMarkerIds,
    setIsLocalPreview,
    setIsBulkMovingSelection,
    updateMapLocationFeatures,
    setIsAddingMapLabel,
    setIsMovingLabel,
    setSelectedLabelId,
    setSelectedRegionId,
    setPreviewMode,
    setPlayerCoords,
    setPlayerBearing,
    setTrainerConnected,
    setLocationEnabled,
    setTeleportEnabled,
    toggleFollowEnabled,
    setFollowEnabled,
    setTooltipDelayFinished,
    setPlayerLocationDeltas,
    setLastSelectedCategory,
    setLastSelectedSubcategory,
    toggleLayerVisibility,
    setLayerVisibility,
    toggleHeatmapEventVisibility,
    setHeatmapEventVisibility,
    toggleAllHeatmapEventsVisibility,
    clearFog,
    addFogCircles,
    hydrateFogCircles,
    updateMapObject3dData,
} = mapDataSlice.actions;

export default mapDataSlice.reducer;

export const selectMapData = (state: { mapData: MapDataState }) => state.mapData.mapData;
export const selectTrackedCompassPin = (state: { mapData: MapDataState }) => state.mapData.trackedCompassPin;
export const selectTrainerConnected = (state: { mapData: MapDataState }) => state.mapData.trainerConnected;

const selectMapDataFeatures = (state: { mapData: MapDataState }) => state.mapData.mapData?.geojson?.features;

export const selectTrackedCompassLocation = createSelector(
    [selectMapDataFeatures, selectTrackedCompassPin],
    (features, trackedPin) =>
        trackedPin ? features?.find(feature => feature.properties.externalId === trackedPin.externalId) : undefined,
);

export const selectMapMetadata = (state: { mapData: MapDataState }) => state.mapData.mapData?.metadata || null;

const EMPTY_HERO_ACTIONS: ReadonlyArray<MapDataHeroAction> = Object.freeze([]);

export const selectHeroActions = (state: { mapData: MapDataState }) =>
    state.mapData.mapData?.heroActions ?? EMPTY_HERO_ACTIONS;

export const selectHeroActionById = (
    state: { mapData: MapDataState; clientContext?: { previewHeroAction?: MapDataHeroAction | null } },
    id: string | null,
) => {
    if (!id) return null;
    const heroAction = selectHeroActions(state).find(a => a.externalId === id);
    if (heroAction) return heroAction;
    const previewHeroAction = state.clientContext?.previewHeroAction;
    return previewHeroAction?.externalId === id ? previewHeroAction : null;
};

export const selectMapName = (state: { mapData: MapDataState }) => state.mapData.mapData?.metadata.name;

export const selectMapList = (state: { mapData: MapDataState }) => state.mapData.mapInfo;

export const selectGroupedCategories = (state: { mapData: MapDataState }) => state.mapData.groupedCategories;

export const selectMapCategories = (state: { mapData: MapDataState }) => state.mapData.mapData?.metadata.categories;

export const selectMapSubcategories = (state: { mapData: MapDataState }) =>
    state.mapData.mapData?.metadata.subcategories;

export const selectMapConfig = (state: { mapData: MapDataState }) => state.mapData.mapConfig;

export const selectExternalMapId = (state: { mapData: MapDataState }) => state.mapData.mapConfig?.externalId;

export const selectTitleSlug = (state: { mapData: MapDataState }) => state.mapData.mapConfig?.titleSlug;

export const selectTitleName = (state: { mapData: MapDataState }) => state.mapData.mapConfig?.titleName;

export const selectTitleId = (state: { mapData: MapDataState }) => state.mapData.mapConfig?.titleId;

export const selectSteamId = (state: { mapData: MapDataState }) => state.mapData.mapConfig?.steamId;

export const selectFilterState = (state: { mapData: MapDataState }) => state.mapData.filters;

export const selectLocationCategories = (state: { mapData: MapDataState }) => state.mapData.locationCategories;

export const selectMapId = (state: { mapData: MapDataState }) => {
    return state.mapData?.mapInfo?.find?.(map => map.externalId === state.mapData.mapConfig?.externalId)?.id || null;
};

export const selectPlayerLocationDeltas = (state: { mapData: MapDataState }) =>
    state.mapData.mapData?.metadata.playerLocationDeltas;

export const selectIsCategoryHidden =
    (categoryId: string) =>
    (state: { mapData: MapDataState }): boolean => {
        if (!state.mapData.mapData) return false;

        // Get all subcategories for this category
        const categorySubcategories = state.mapData.mapData.metadata.subcategories
            .filter(sub => sub.categoryExternalId === categoryId)
            .map(sub => sub.externalId);

        // Handle custom pins category specially
        if (categoryId === CUSTOM_PINS_CATEGORY_ID) {
            categorySubcategories.push(CUSTOM_PINS_CATEGORY_ID, VIDEO_PINS_SUBCATEGORY_ID);
        }

        // Category is hidden if ALL its subcategories are hidden
        return (
            categorySubcategories.length > 0 &&
            categorySubcategories.every(subId => selectFilterState(state).hiddenSubcategories.includes(subId))
        );
    };

export const selectIsSubcategoryHidden =
    (subcategoryId: string) =>
    (state: { mapData: MapDataState }): boolean => {
        return selectFilterState(state).hiddenSubcategories.includes(subcategoryId);
    };

export const selectLastSelectedCategoryId = (state: { mapData: MapDataState }) => state.mapData.lastSelectedCategoryId;
export const selectLastSelectedSubcategoryId = (state: { mapData: MapDataState }) =>
    state.mapData.lastSelectedSubcategoryId;

export const selectCategoryVisibility = createSelector([selectFilterState], filters => ({
    hiddenSubcategories: new Set(filters.hiddenSubcategories),
    hideAll: filters.hideAll,
    teleportOnly: filters.teleportOnly,
    unfoundOnly: filters.unfoundOnly,
    searchTerm: filters.searchTerm.toLowerCase().trim(),
}));

export const selectFoundPins = (state: { mapData: MapDataState }) => state.mapData.foundPins;

export const selectCustomPins = (state: { mapData: MapDataState }) => state.mapData.customPins;

export const selectSelectedMarkerId = (state: { mapData: MapDataState }) => state.mapData.selectedMarkerId;

export const selectIsMarkerSelected =
    (markerId: string) =>
    (state: { mapData: MapDataState }): boolean => {
        return state.mapData.selectedMarkerId === markerId;
    };

export const selectIsAddingCustomLocation = (state: { mapData: MapDataState }) => state.mapData.isAddingCustomLocation;

export const selectIsEditingCustomPin = (state: { mapData: MapDataState }) =>
    state.mapData.editingCustomPinState?.isEditing;

export const selectEditingPinId = (state: { mapData: MapDataState }) => state.mapData.editingCustomPinState.pinId;

export const selectIsEditingNewlyCreatedPin = (state: { mapData: MapDataState }) =>
    state.mapData.editingCustomPinState?.isNewlyCreated;

export const selectCustomPinEditingState = (state: { mapData: MapDataState }) => state.mapData.editingCustomPinState;
export const selectIsInCreatorMode = (state: { mapData: MapDataState }) => state.mapData.isInCreatorMode;
export const selectLocationEnabled = (state: { mapData: MapDataState }) => state.mapData.locationEnabled;
export const selectTeleportEnabled = (state: { mapData: MapDataState }) => state.mapData.teleportEnabled;
export const selectFollowEnabled = (state: { mapData: MapDataState }) => state.mapData.followEnabled;
export const selectTooltipDelayFinished = (state: { mapData: MapDataState }) => state.mapData.tooltipDelayFinished;
export const selectPlayerCoords = (state: { mapData: MapDataState }) => state.mapData.playerCoords;
export const selectPlayerBearing = (state: { mapData: MapDataState }) => state.mapData.playerBearing;

const searchResultFilterOptions = { includeHiddenSubcategoriesWhenSearching: true };

export const selectFilteredLocations = createSelector(
    [
        selectMapDataFeatures,
        selectFilterState,
        selectFoundPins,
        (state: { mapData: MapDataState }) => state.mapData.locationCategories,
        selectSelectedMarkerId,
        selectMapData,
        (state: { clientContext: { activeCategoryId: string | null } }) => state.clientContext.activeCategoryId,
    ],
    (features, filters, foundPins, locationCategories, selectedMarkerId, mapData, activeCategoryId) => {
        if (!features || !mapData) return [];

        return getFilteredFeatures(
            mapData,
            filters,
            foundPins,
            locationCategories,
            selectedMarkerId,
            activeCategoryId,
            false,
            searchResultFilterOptions,
        );
    },
);

export const selectFilteredMapLocations = createSelector(
    [
        selectMapDataFeatures,
        selectFilterState,
        selectFoundPins,
        (state: { mapData: MapDataState }) => state.mapData.locationCategories,
        selectMapData,
        (state: { clientContext: { activeCategoryId: string | null } }) => state.clientContext.activeCategoryId,
    ],
    (features, filters, foundPins, locationCategories, mapData, activeCategoryId) => {
        if (!features || !mapData) return [];

        return getFilteredFeatures(
            mapData,
            filters,
            foundPins,
            locationCategories,
            null,
            activeCategoryId,
            false,
            searchResultFilterOptions,
        );
    },
);

export const selectVisibleFeatures = createSelector([selectFilteredLocations], filteredLocations =>
    filteredLocations.filter(location => !location.properties.hidden),
);

export const selectFoundPinsCount = createSelector(
    [selectFoundPins],
    foundPins => Object.values(foundPins).filter(Boolean).length,
);

export const selectTotalPinsCount = createSelector([selectMapDataFeatures], features => features?.length || 0);

export const selectCurrentMapInfo = createSelector(
    [selectMapList, selectExternalMapId],
    (maps, currentMapExternalId) => {
        return maps?.find(map => map.externalId === currentMapExternalId);
    },
);

export const selectMapSupportsLiveLocation = createSelector([selectCurrentMapInfo], (mapInfo): boolean => {
    if (!mapInfo) return false;
    return (mapInfo.flags & CatalogGameMapFlags.SupportsLiveLocation) !== 0;
});

export const selectMarkerProps = createSelector(
    [selectMapData, selectFoundPins, selectSelectedMarkerId],
    (mapData, foundPins, selectedMarkerId) => ({
        features: mapData?.geojson.features || [],
        foundPins,
        selectedMarkerId,
    }),
);

export const selectIsAddingMapLocation = (state: { mapData: MapDataState }) => state.mapData.isAddingMapLocation;
export const selectIsEditingMapLocation = (state: { mapData: MapDataState }) => state.mapData.isEditingMapLocation;
export const selectEditingMapLocationId = (state: { mapData: MapDataState }) => state.mapData.editingMapLocationId;
export const selectIsMovingLocation = (state: { mapData: MapDataState }) => state.mapData.isMovingLocation;
export const selectMovingLocationId = (state: { mapData: MapDataState }) => state.mapData.movingLocationId;
export const selectIsBulkMovingLocations = (state: { mapData: MapDataState }) => state.mapData.isBulkMovingLocations;
export const selectIsBulkSelectingPins = (state: { mapData: MapDataState }) => state.mapData.isBulkSelectingPins;
export const selectIsBulkMovingSelection = (state: { mapData: MapDataState }) => state.mapData.isBulkMovingSelection;
export const selectSelectedMarkerIds = (state: { mapData: MapDataState }) => state.mapData.selectedMarkerIds;
export const selectIsAddingMapLabel = (state: { mapData: MapDataState }) => state.mapData.isAddingMapLabel;
export const selectIsMovingLabel = (state: { mapData: MapDataState }) => state.mapData.isMovingLabel;
export const selectMovingLabelId = (state: { mapData: MapDataState }) => state.mapData.movingLabelId;
export const selectSelectedLabelId = (state: { mapData: MapDataState }) => state.mapData.selectedLabelId;
export const selectSelectedRegionId = (state: { mapData: MapDataState }) => state.mapData.selectedRegionId;
export const selectPreviewMode = (state: { mapData: MapDataState }) => state.mapData.previewMode;
export const selectLayerVisibility = (state: { mapData: MapDataState }) => state.mapData.layerVisibility;

export const selectHeatmapEventVisibility = (state: { mapData: MapDataState }) => state.mapData.heatmapEventVisibility;

export const selectSelectedHeatmapEvent = (state: { mapData: MapDataState }) => state.mapData.selectedHeatmapEvent;

export const selectFogOfWarEnabled = (state: { mapData: MapDataState }) => state.mapData.layerVisibility.fogOfWar;

/** Fog UX + rendering: layer toggle on and map supports live location (catalog flag). */
export const selectFogOfWarActive = createSelector(
    [selectFogOfWarEnabled, selectMapSupportsLiveLocation],
    (fogOfWarEnabled, supportsLiveLocation) => fogOfWarEnabled && supportsLiveLocation,
);

export const selectFogCircles = (state: { mapData: MapDataState }) => state.mapData.fogOfWar.circles;

export const selectFogHydrationVersion = (state: { mapData: MapDataState }) => state.mapData.fogOfWar.hydrationVersion;

export const selectHiddenRegions = (state: { mapData: MapDataState }) => state.mapData.filters.hiddenRegions;

export const selectHasLabels = (state: { mapData: MapDataState }) => {
    return (state.mapData.mapData?.labels?.features?.length ?? 0) > 0;
};

export const selectHasRegions = (state: { mapData: MapDataState }) => {
    return (state.mapData.mapData?.regions?.features?.length ?? 0) > 0;
};

export const selectHas3dObjects = (state: { mapData: MapDataState }) => {
    return (state.mapData.mapData?.objects3d?.features?.length ?? 0) > 0;
};

export const selectMapSupportsTeleport = (state: { mapData: MapDataState }): boolean => {
    return state.mapData.mapData?.metadata.supportsTeleport ?? false;
};
